import { FC } from "react"
import { Trash2 } from "lucide-react"
import { FieldDefinition } from "../../../../../../constants/connector-field-types"
import ValueMappingInput from "../value-mapping-input"

interface ValueTransformEditorProps {
  mapKey: string
  isIncoming: boolean
  isSelectField: boolean
  fieldDef: FieldDefinition | null | undefined
  valueTransforms: Record<string, any>
  hasTransforms: boolean
  onUpdateValueTransform: (
    key: string,
    sourceValue: string,
    targetValue: string
  ) => void
  onRemoveValueTransform: (key: string, sourceValue: string) => void
  onRemoveAllValueTransforms: (key: string) => void
  onCollapseField: (key: string) => void
}

const ValueTransformEditor: FC<ValueTransformEditorProps> = ({
  mapKey,
  isIncoming,
  isSelectField,
  fieldDef,
  valueTransforms,
  hasTransforms,
  onUpdateValueTransform,
  onRemoveValueTransform,
  onRemoveAllValueTransforms,
  onCollapseField
}) => {
  return (
    <div className="mt-3 pl-4 border-l-2 border-blue-300 space-y-2">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-medium text-gray-700">
            Value Mapping{" "}
            {hasTransforms && (
              <span className="text-purple-600">(configured)</span>
            )}
          </div>
          <p className="text-xs text-gray-600">
            {isIncoming
              ? "Map CRM values to Banking Bridge values (e.g., '1' → 'purchase')"
              : "Map Banking Bridge values to CRM values (e.g., 'purchase' → 1)"}
          </p>
        </div>
        {hasTransforms && (
          <button
            type="button"
            onClick={() => {
              onRemoveAllValueTransforms(mapKey)
              onCollapseField(mapKey)
            }}
            className="text-red-600 hover:text-red-800 text-xs"
          >
            Remove All Mappings
          </button>
        )}
      </div>

      {/* For select fields, show predefined options */}
      {isSelectField && fieldDef?.options && (
        <div className="space-y-2">
          <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-600 pb-1">
            <div className="col-span-2">
              {isIncoming ? "CRM Value" : "BB Value"}
            </div>
            <div className="col-span-2">
              {isIncoming ? "BB Value" : "CRM Value"}
            </div>
            <div></div>
          </div>

          {fieldDef.options.map((option, optIndex) => {
            let leftValue = ""
            let rightValue = option.value

            if (isIncoming) {
              // Find the CRM value (key) that maps to this BB value (option.value)
              const entry = Object.entries(valueTransforms).find(
                ([, bbVal]) => bbVal === option.value
              )
              leftValue = entry ? entry[0] : ""
            } else {
              leftValue = option.value
              rightValue = valueTransforms[option.value] || ""
            }

            // Use optIndex for stable key instead of dynamic values
            const stableKey = `${mapKey}-transform-${optIndex}`

            return (
              <div
                key={stableKey}
                className="grid grid-cols-5 gap-2 items-center"
              >
                <div className="col-span-2">
                  <input
                    key={`${mapKey}-left-${option.value}`}
                    type="text"
                    value={leftValue}
                    onChange={
                      isIncoming
                        ? e =>
                            onUpdateValueTransform(
                              mapKey,
                              option.value,
                              e.target.value
                            )
                        : undefined
                    }
                    readOnly={!isIncoming}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                    title={option.label}
                    placeholder={isIncoming ? "CRM value (e.g., 1)" : ""}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    key={`${mapKey}-right-${option.value}`}
                    type="text"
                    value={rightValue}
                    onChange={
                      !isIncoming
                        ? e =>
                            onUpdateValueTransform(
                              mapKey,
                              option.value,
                              e.target.value
                            )
                        : undefined
                    }
                    readOnly={isIncoming}
                    placeholder={!isIncoming ? "CRM value/code" : ""}
                    className={`w-full px-2 py-1 border border-gray-300 rounded text-sm ${
                      isIncoming ? "bg-gray-50" : ""
                    }`}
                    title={isIncoming ? option.label : ""}
                  />
                </div>
                <div>
                  {leftValue && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isIncoming) {
                          // For incoming, we need to find the CRM key to remove
                          const entry = Object.entries(valueTransforms).find(
                            ([, bbVal]) => bbVal === option.value
                          )
                          if (entry) {
                            onRemoveValueTransform(mapKey, entry[0])
                          }
                        } else {
                          onRemoveValueTransform(mapKey, option.value)
                        }
                      }}
                      className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                      title="Clear value"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {/* Custom mappings - only show if not already in predefined options */}
          {Object.entries(valueTransforms)
            .filter(([k, v]) => {
              if (k === "default") return false
              const checkValue = isIncoming ? v : k
              return !fieldDef?.options?.some(opt => opt.value === checkValue)
            })
            .map(([sourceVal, targetVal]) => (
              <div
                key={sourceVal}
                className="grid grid-cols-5 gap-2 items-center bg-yellow-50 p-1 rounded"
              >
                <div className="col-span-2">
                  <input
                    type="text"
                    value={isIncoming ? String(targetVal) : sourceVal}
                    readOnly
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={isIncoming ? sourceVal : String(targetVal)}
                    onChange={e =>
                      onUpdateValueTransform(mapKey, sourceVal, e.target.value)
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => onRemoveValueTransform(mapKey, sourceVal)}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                    title="Remove mapping"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}

          <ValueMappingInput
            bbField={mapKey}
            onAdd={(val1, val2) => {
              onUpdateValueTransform(
                mapKey,
                isIncoming ? val2 : val1,
                isIncoming ? val1 : val2
              )
            }}
          />
        </div>
      )}

      {/* For non-select fields */}
      {!isSelectField && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 mb-2">
            {isIncoming
              ? "Map CRM values to BB values (e.g., 'excellent' → 800)"
              : "Map BB values to CRM values (e.g., 800 → 'excellent')"}
          </p>
          {Object.entries(valueTransforms)
            .filter(([k]) => k !== "default")
            .map(([sourceVal, targetVal]) => (
              <div
                key={sourceVal}
                className="grid grid-cols-5 gap-2 items-center"
              >
                <div className="col-span-2">
                  <input
                    type="text"
                    value={sourceVal}
                    readOnly
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={String(targetVal)}
                    onChange={e =>
                      onUpdateValueTransform(mapKey, sourceVal, e.target.value)
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => onRemoveValueTransform(mapKey, sourceVal)}
                    className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                    title="Remove mapping"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          <ValueMappingInput
            bbField={mapKey}
            onAdd={(val1, val2) => onUpdateValueTransform(mapKey, val1, val2)}
          />
        </div>
      )}

      {/* Default value */}
      <div className="grid grid-cols-5 gap-2 items-center pt-2 border-t">
        <div className="col-span-2 text-sm text-gray-700 font-medium">
          Default/Fallback
        </div>
        <div className="col-span-2">
          <input
            type="text"
            value={valueTransforms["default"] || ""}
            onChange={e =>
              onUpdateValueTransform(mapKey, "default", e.target.value)
            }
            placeholder={isIncoming ? "Default BB value" : "Default CRM value"}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        <div></div>
      </div>
    </div>
  )
}

export default ValueTransformEditor
